import React from 'react'
import { SideNavigatorContainer, ProgressSectionBlockContainer, StepIndicatorContainer, ErrorDot } from './style'
import Text from 'particles/Text'
import Icon from 'particles/Icon'
import Tooltip from 'particles/Tooltip'
import IconButton from 'particles/Button/IconButton'

interface StepIndicatorProps {
    isCompleted: boolean
    isCommented?: boolean
    isActive: boolean
    step: string | number
    isDisabled?: boolean
    isError?: boolean
}

export interface StepOptions {
    title: string
    subtext?: string
    isCompleted: boolean
    isCommented?: boolean
    isDisabled?: boolean
    isError?: boolean
    id?: string | number
}

interface ProgressSectionBlockProps extends StepOptions {
    step: string | number
    isActive: boolean
    isError?: boolean
    isExpanded: boolean
    isDisabled?: boolean
    onClick?: () => void
    isEditable: boolean
    onRemoveStep?: () => void
    isDisabledClickable?: boolean
    isHorizontal?: boolean
}

interface SideNavigatorProps {
    stepOptions?: StepOptions[]
    isExpanded?: boolean
    onSelectStep?: (index: number, isDisabled?: boolean) => void
    selected?: number | string | null
    isEditable?: boolean
    onAddStep?: () => void
    onRemoveStep?: (index: number) => void
    isDisabledClickable?: boolean
    isHorizontal?: boolean
    onClickAllOption?: () => void
    className?: string
}

export const ProgressSectionBlock: React.FC<ProgressSectionBlockProps> = ({
    title,
    subtext,
    isCompleted,
    isCommented,
    isActive,
    step,
    isDisabled,
    isError,
    onClick,
    isExpanded = true,
    isEditable,
    onRemoveStep,
    isDisabledClickable = false,
    isHorizontal = false,
}) => {
    return (
        <ProgressSectionBlockContainer
            isExpanded={isExpanded}
            isDisabled={!isDisabledClickable && isDisabled}
            {...(!isEditable && { onClick: onClick })}
            className={`progress-block ${isHorizontal ? 'horizontal' : ''}`}
        >
            <div className="indicator-container">
                <Tooltip title={title} label={subtext} placement="right" disableHoverListener={isExpanded}>
                    <span>
                        <StepIndicator {...{ isActive, isCommented, isCompleted, step, isDisabled }} />
                    </span>
                </Tooltip>
                <div className="step-connector" />
            </div>

            {isExpanded && (
                <div className="step-details-container">
                    <Text
                        scale="bodyS700"
                        color={isError ? 'error' : isDisabled ? 'disable' : 'primary'}
                        className="step-title"
                    >
                        {title}
                        {isError && <ErrorDot className="error-dot" />}
                    </Text>
                    <Text scale="caption400" color={isDisabled ? 'disable' : 'secondary'}>
                        {subtext}
                    </Text>
                </div>
            )}
            {isEditable && isExpanded && (
                <IconButton icon="x" form="icon" size="xs" color="neutral" onClick={onRemoveStep} />
            )}
        </ProgressSectionBlockContainer>
    )
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ isCommented, isActive, isCompleted, step, isDisabled }) => {
    const getColor = () => {
        if (isDisabled) return 'disable'
        if (isActive) return 'primaryInverse'
        if (isCommented) return 'warning'
        if (isCompleted) return 'positive'
        return 'primary'
    }

    const getContent = () => {
        if (isActive || !(isCommented || isCompleted)) {
            return (
                <Text scale="bodyM700" color={getColor()} className="step">
                    {step}
                </Text>
            )
        }
        return <Icon name={isCommented ? 'chatText' : 'check'} color={getColor()} size="12px" />
    }

    return (
        <>
            <StepIndicatorContainer
                isCommented={isCommented}
                isActive={isActive}
                isCompleted={isCompleted}
                isDisabled={isDisabled}
            >
                {getContent()}
            </StepIndicatorContainer>
        </>
    )
}

const AddStep: React.FC<any> = ({ onClick, title, isExpanded }) => {
    return (
        <div onClick={onClick} className="add-step">
            <StepIndicatorContainer isAddStepAction>
                <Icon name="plus" color="neutralAction" />
            </StepIndicatorContainer>
            {isExpanded && (
                <Text scale="bodyM500" color="neutralAction" as="div">
                    {title}
                </Text>
            )}
        </div>
    )
}

const SideNavigator: React.FC<SideNavigatorProps> = ({
    stepOptions,
    isExpanded = true,
    onSelectStep,
    selected,
    isEditable = false,
    onAddStep,
    onRemoveStep,
    children,
    isDisabledClickable = false,
    isHorizontal = false,
    onClickAllOption,
    className,
}) => {
    return (
        <SideNavigatorContainer className={`${className} ${isHorizontal ? 'horizontal' : ''}`}>
            {children
                ? React.Children.map(children, (child: React.ReactNode) => {
                      if (React.isValidElement<ProgressSectionBlockProps>(child)) {
                          return React.cloneElement(child, {
                              isExpanded: isExpanded,
                              isEditable: isEditable,
                          })
                      }
                      return null
                  })
                : stepOptions?.map((option, index) => (
                      <ProgressSectionBlock
                          {...option}
                          key={index}
                          step={index + 1}
                          isExpanded={isExpanded}
                          isActive={index === selected}
                          onClick={() =>
                              onClickAllOption ? onClickAllOption() : onSelectStep?.(index, option.isDisabled)
                          }
                          isEditable={isEditable}
                          isDisabled={!isEditable && option.isDisabled}
                          onRemoveStep={() => onRemoveStep?.(index)}
                          isDisabledClickable={isDisabledClickable}
                          isHorizontal={isHorizontal}
                      />
                  ))}
            {isEditable && <AddStep onClick={onAddStep} title="Add Section" isExpanded={isExpanded} />}
        </SideNavigatorContainer>
    )
}

export default SideNavigator
